import { CodeBlock } from '../../components/developers/CodeBlock';
import { ExternalLink } from 'lucide-react';

export default function ApiReference() {
  return (
    <article className="prose prose-slate max-w-none">
      <h1>API Reference</h1>
      <p className="lead">Complete REST API documentation for the Opair AI Governance Platform.</p>

      <div className="not-prose my-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-indigo-900">Interactive API Explorer</h3>
          <p className="text-indigo-700 text-sm mt-1">
            Try out API endpoints directly in the Swagger UI documentation.
          </p>
        </div>
        <a
          href="/api/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Open Swagger UI
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <h2>Base URL</h2>
      <CodeBlock language="bash" code={`https://api.opair.example.com/v1`} />

      <h2>Authentication</h2>
      <p>All API requests require authentication via one of the following methods:</p>
      <CodeBlock
        language="bash"
        code={`# API Key (workload-scoped)
curl -H "X-API-Key: your-api-key" https://api.opair.example.com/v1/health

# JWT Bearer Token
curl -H "Authorization: Bearer your-jwt-token" https://api.opair.example.com/v1/health

# Personal Access Token (PAT)
curl -H "Authorization: Bearer pat_abc123..." https://api.opair.example.com/v1/health`}
      />

      <CodeBlock
        title="TypeScript (SDK)"
        language="typescript"
        code={`import { OpairClient } from '@opair/sdk';

// API Key
const client = new OpairClient({
  apiKey: 'your-api-key',
});

// Or Bearer Token / PAT
const clientWithToken = new OpairClient({
  accessToken: 'your-jwt-or-pat-token',
});`}
      />

      <h2>Endpoints Overview</h2>

      <h3>Health &amp; System</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/health</code>
            </td>
            <td>Check API health status</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/ready</code>
            </td>
            <td>Readiness probe</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/system/health/detailed</code>
            </td>
            <td>Detailed system health with dependency status</td>
          </tr>
        </tbody>
      </table>

      <h3>Authentication</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/auth/login</code>
            </td>
            <td>Authenticate with email/password</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/auth/refresh</code>
            </td>
            <td>Refresh access token</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/auth/logout</code>
            </td>
            <td>Revoke refresh token</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/auth/register</code>
            </td>
            <td>Self-service registration</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/auth/password/reset</code>
            </td>
            <td>Request password reset</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/auth/password/change</code>
            </td>
            <td>Change current password</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/auth/mfa/setup</code>
            </td>
            <td>Initialize TOTP MFA setup</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/auth/mfa/verify</code>
            </td>
            <td>Verify TOTP code</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/auth/mfa/validate</code>
            </td>
            <td>Validate MFA token during login</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/auth/mfa/disable</code>
            </td>
            <td>Disable MFA</td>
          </tr>
        </tbody>
      </table>

      <h3>Personal Access Tokens (PAT)</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/user/tokens</code>
            </td>
            <td>Create a new PAT (returns raw token once)</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/user/tokens</code>
            </td>
            <td>List active tokens (no raw value)</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/user/tokens/:tokenId</code>
            </td>
            <td>Revoke a token</td>
          </tr>
        </tbody>
      </table>

      <h3>Organizations</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/orgs</code>
            </td>
            <td>List organizations</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/orgs</code>
            </td>
            <td>Create organization</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/orgs/:id</code>
            </td>
            <td>Get organization details</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/orgs/:id</code>
            </td>
            <td>Update organization settings</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/orgs/:id</code>
            </td>
            <td>Delete organization</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/orgs/:id/activate</code>
            </td>
            <td>Activate suspended org</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/orgs/:id/suspend</code>
            </td>
            <td>Suspend organization</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/orgs/:orgId/mfa-compliance</code>
            </td>
            <td>MFA compliance status for org users</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/orgs/:orgId/mfa-enforcement</code>
            </td>
            <td>Update org MFA enforcement policy</td>
          </tr>
        </tbody>
      </table>

      <h3>Workloads</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/workloads</code>
            </td>
            <td>List workloads</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/workloads</code>
            </td>
            <td>Create workload</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/workloads/:id</code>
            </td>
            <td>Get workload details</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/workloads/:id</code>
            </td>
            <td>Update workload</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/workloads/:id</code>
            </td>
            <td>Delete workload</td>
          </tr>
        </tbody>
      </table>

      <h3>API Keys (Workload-scoped)</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/workloads/:id/api-keys</code>
            </td>
            <td>List API keys for workload</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/workloads/:id/api-keys</code>
            </td>
            <td>Create API key</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/workloads/:id/api-keys/rotate</code>
            </td>
            <td>Rotate API key</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/workloads/:id/api-keys/:keyId</code>
            </td>
            <td>Revoke API key</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/workloads/:id/api-keys/validate</code>
            </td>
            <td>Validate an API key</td>
          </tr>
        </tbody>
      </table>

      <h3>Policies</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/policies</code>
            </td>
            <td>List all policies</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/policies</code>
            </td>
            <td>Create a policy</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/policies/:id</code>
            </td>
            <td>Get policy by ID</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/policies/:id</code>
            </td>
            <td>Update a policy</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/policies/:id</code>
            </td>
            <td>Delete a policy</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/policies/:id/versions</code>
            </td>
            <td>List policy version history</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/policies/:id/versions/:version</code>
            </td>
            <td>Get specific version</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/policies/:id/versions/:version/restore</code>
            </td>
            <td>Restore a version</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/policies/:id/versions/diff</code>
            </td>
            <td>Diff two versions</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/policies/playground/dry-run</code>
            </td>
            <td>Test policy evaluation (sandbox)</td>
          </tr>
        </tbody>
      </table>

      <h3>Policy Templates</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/policy-templates</code>
            </td>
            <td>List templates</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/policy-templates/:id</code>
            </td>
            <td>Get template</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/policy-templates/from-template</code>
            </td>
            <td>Create policy from template</td>
          </tr>
        </tbody>
      </table>

      <h3>Policy Approvals</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/policy-approvals</code>
            </td>
            <td>List approval requests</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/policy-approvals/pending</code>
            </td>
            <td>List pending approvals</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/policy-approvals</code>
            </td>
            <td>Submit for approval</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/policy-approvals/:id/approve</code>
            </td>
            <td>Approve a policy change</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/policy-approvals/:id/reject</code>
            </td>
            <td>Reject a policy change</td>
          </tr>
        </tbody>
      </table>

      <h3>Providers</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/providers</code>
            </td>
            <td>List providers</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/providers</code>
            </td>
            <td>Register a provider</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/providers/:id</code>
            </td>
            <td>Get provider details</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/providers/:id</code>
            </td>
            <td>Update provider</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/providers/:id</code>
            </td>
            <td>Remove provider</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/providers/:id/models</code>
            </td>
            <td>List provider models</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/providers/:id/test</code>
            </td>
            <td>Test provider connectivity</td>
          </tr>
        </tbody>
      </table>

      <h3>Bundles</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/bundles</code>
            </td>
            <td>List all bundles</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/bundles</code>
            </td>
            <td>Upload a new bundle</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/bundles/:id</code>
            </td>
            <td>Get bundle by ID</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/bundles/:id</code>
            </td>
            <td>Update bundle metadata</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/bundles/:id</code>
            </td>
            <td>Delete a bundle</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/bundles/:workloadId/rollback</code>
            </td>
            <td>Rollback to previous bundle</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/bundles/:workloadId/rollback/history</code>
            </td>
            <td>Rollback history</td>
          </tr>
        </tbody>
      </table>

      <h3>Routes (Traffic Routing)</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/routes</code>
            </td>
            <td>List routing rules</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/routes</code>
            </td>
            <td>Create routing rule</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/routes/:id</code>
            </td>
            <td>Get routing rule</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/routes/:id</code>
            </td>
            <td>Update routing rule</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/routes/:id</code>
            </td>
            <td>Delete routing rule</td>
          </tr>
        </tbody>
      </table>

      <h3>Audit Events</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/audit-events</code>
            </td>
            <td>List audit events (filterable)</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/audit-events/:id</code>
            </td>
            <td>Get audit event detail</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/audit-events/workload/:workloadId</code>
            </td>
            <td>Events for a specific workload</td>
          </tr>
        </tbody>
      </table>

      <h3>Compliance Reports</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/compliance/reports</code>
            </td>
            <td>List compliance reports</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/compliance/reports</code>
            </td>
            <td>Generate a compliance report</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/compliance/reports/:id</code>
            </td>
            <td>Get report details</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/compliance/reports/:id/download</code>
            </td>
            <td>Download report as PDF</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/compliance/reports/:id/certify</code>
            </td>
            <td>Certify report against framework</td>
          </tr>
        </tbody>
      </table>

      <h3>Data Export (GDPR)</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/org/data-export/request</code>
            </td>
            <td>Request a data export (GDPR SAR)</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/org/data-export/:id/status</code>
            </td>
            <td>Check export status</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/org/data-export/:id/download</code>
            </td>
            <td>Download exported data</td>
          </tr>
        </tbody>
      </table>

      <h3>Workflows</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/workflows</code>
            </td>
            <td>List workflows</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/workflows</code>
            </td>
            <td>Create workflow</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/workflows/:id</code>
            </td>
            <td>Get workflow details</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/workflows/:id</code>
            </td>
            <td>Update workflow</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/workflows/:id</code>
            </td>
            <td>Delete workflow</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/workflows/:id/trigger</code>
            </td>
            <td>Trigger workflow execution</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/workflows/:id/validate</code>
            </td>
            <td>Validate workflow definition</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/workflows/:id/executions</code>
            </td>
            <td>List workflow executions</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/workflows/:id/executions/:execId</code>
            </td>
            <td>Get execution details</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/workflows/templates</code>
            </td>
            <td>List workflow templates</td>
          </tr>
        </tbody>
      </table>

      <h3>Support Tickets</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/support/tickets</code>
            </td>
            <td>List support tickets</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/support/tickets</code>
            </td>
            <td>Create a support ticket</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/support/tickets/:id</code>
            </td>
            <td>Get ticket details</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/support/tickets/:id</code>
            </td>
            <td>Update ticket</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/support/tickets/:id/comments</code>
            </td>
            <td>Add comment to ticket</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/support/tickets/:id</code>
            </td>
            <td>Close/delete ticket</td>
          </tr>
        </tbody>
      </table>

      <h3>Invitations</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/invitations</code>
            </td>
            <td>List invitations</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/invitations</code>
            </td>
            <td>Create an invitation</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/invitations/:id/accept</code>
            </td>
            <td>Accept invitation</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/invitations/:id/revoke</code>
            </td>
            <td>Revoke invitation</td>
          </tr>
        </tbody>
      </table>

      <h3>SSO &amp; SCIM</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/org/sso/:orgId/initiate</code>
            </td>
            <td>Initiate SSO login</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/org/sso/callback</code>
            </td>
            <td>SSO callback</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/org/sso/detect</code>
            </td>
            <td>Detect SSO provider by email</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/scim/v2/Users</code>
            </td>
            <td>SCIM: List users</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/scim/v2/Users</code>
            </td>
            <td>SCIM: Provision user</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/scim/v2/Groups</code>
            </td>
            <td>SCIM: List groups</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/scim/v2/Groups</code>
            </td>
            <td>SCIM: Create group</td>
          </tr>
        </tbody>
      </table>

      <h3>Configuration</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/config/export</code>
            </td>
            <td>Export organization config (YAML/JSON)</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/config/import</code>
            </td>
            <td>Import organization config</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/config/import/dry-run</code>
            </td>
            <td>Dry-run import (preview changes)</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/config-changes</code>
            </td>
            <td>List configuration changes</td>
          </tr>
        </tbody>
      </table>

      <h3>Analytics &amp; Costs</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/analytics/overview</code>
            </td>
            <td>Overview metrics</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/analytics/usage</code>
            </td>
            <td>Usage analytics</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/analytics/cost</code>
            </td>
            <td>Cost analytics</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/analytics/latency</code>
            </td>
            <td>Latency analytics</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/costs/recommendations</code>
            </td>
            <td>Cost optimization recommendations</td>
          </tr>
        </tbody>
      </table>

      <h3>Security</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/security/alerts</code>
            </td>
            <td>Security alerts</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/security/anomalies</code>
            </td>
            <td>Anomaly detections</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/security/risk-scores</code>
            </td>
            <td>Risk scores</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/security/shadow-ai</code>
            </td>
            <td>Shadow AI detections</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/security/rules</code>
            </td>
            <td>Security rules</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/security/rules</code>
            </td>
            <td>Create security rule</td>
          </tr>
        </tbody>
      </table>

      <h3>Webhooks</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/webhooks</code>
            </td>
            <td>List webhooks</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/webhooks</code>
            </td>
            <td>Create webhook</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/webhooks/:id</code>
            </td>
            <td>Get webhook details</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/webhooks/:id</code>
            </td>
            <td>Update webhook</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/webhooks/:id</code>
            </td>
            <td>Delete webhook</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/webhooks/:id/test</code>
            </td>
            <td>Send test event</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/webhooks/:id/deliveries</code>
            </td>
            <td>Delivery history</td>
          </tr>
        </tbody>
      </table>

      <h3>SIEM Integration</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/integrations/siem</code>
            </td>
            <td>List SIEM integrations</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/integrations/siem</code>
            </td>
            <td>Create SIEM integration</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/integrations/siem/:id</code>
            </td>
            <td>Get integration</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/integrations/siem/:id</code>
            </td>
            <td>Update integration</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/integrations/siem/:id</code>
            </td>
            <td>Delete integration</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/integrations/siem/:id/test</code>
            </td>
            <td>Test SIEM connection</td>
          </tr>
        </tbody>
      </table>

      <h3>Connectors</h3>
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th>Endpoint</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/connectors</code>
            </td>
            <td>List connectors</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/connectors</code>
            </td>
            <td>Create connector</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/connectors/:id</code>
            </td>
            <td>Get connector details</td>
          </tr>
          <tr>
            <td>
              <code>PATCH</code>
            </td>
            <td>
              <code>/v1/connectors/:id</code>
            </td>
            <td>Update connector</td>
          </tr>
          <tr>
            <td>
              <code>DELETE</code>
            </td>
            <td>
              <code>/v1/connectors/:id</code>
            </td>
            <td>Delete connector</td>
          </tr>
          <tr>
            <td>
              <code>POST</code>
            </td>
            <td>
              <code>/v1/connectors/:id/test</code>
            </td>
            <td>Test connector</td>
          </tr>
          <tr>
            <td>
              <code>GET</code>
            </td>
            <td>
              <code>/v1/connectors/types</code>
            </td>
            <td>Available connector types</td>
          </tr>
        </tbody>
      </table>

      <h2>Response Format</h2>
      <p>All responses use JSON and include standard fields:</p>
      <CodeBlock
        language="json"
        code={`{
  "success": true,
  "data": {
    "id": "policy-123",
    "name": "Content Moderation",
    "version": "1.0.0"
  },
  "meta": {
    "requestId": "req-abc123",
    "timestamp": "2026-01-15T10:30:00Z"
  }
}`}
      />

      <h2>Error Responses</h2>
      <CodeBlock
        language="json"
        code={`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Invalid policy configuration",
    "details": [
      { "field": "name", "message": "Name is required" }
    ]
  },
  "meta": {
    "requestId": "req-abc123",
    "timestamp": "2026-01-15T10:30:00Z"
  }
}`}
      />

      <h2>Example: Create a Personal Access Token</h2>
      <CodeBlock
        title="cURL"
        language="bash"
        code={`curl -X POST "https://api.opair.example.com/v1/user/tokens" \\
  -H "Authorization: Bearer your-jwt-token" \\
  -H "Content-Type: application/json" \\
  -d '{
    "label": "CI/CD Pipeline",
    "scopes": ["read:policies", "write:bundles"],
    "expiresInDays": 90
  }'

# Response (201):
# {
#   "token": "pat_a1b2c3d4e5f6...",  <-- only shown once!
#   "id": "tok-abc123",
#   "label": "CI/CD Pipeline"
# }`}
      />

      <h2>Example: Trigger a Workflow</h2>
      <CodeBlock
        title="cURL"
        language="bash"
        code={`curl -X POST "https://api.opair.example.com/v1/workflows/wf-123/trigger" \\
  -H "Authorization: Bearer your-jwt-token" \\
  -H "Content-Type: application/json" \\
  -d '{ "input": { "policyId": "policy-456" } }'`}
      />

      <h2>Example: Request GDPR Data Export</h2>
      <CodeBlock
        title="cURL"
        language="bash"
        code={`curl -X POST "https://api.opair.example.com/v1/org/data-export/request" \\
  -H "Authorization: Bearer your-jwt-token" \\
  -H "Content-Type: application/json" \\
  -d '{ "userId": "user-789", "format": "json" }'

# Check status:
curl "https://api.opair.example.com/v1/org/data-export/exp-abc/status" \\
  -H "Authorization: Bearer your-jwt-token"

# Download when ready:
curl -O "https://api.opair.example.com/v1/org/data-export/exp-abc/download" \\
  -H "Authorization: Bearer your-jwt-token"`}
      />

      <h2>Rate Limiting</h2>
      <p>API requests are rate limited based on your plan:</p>
      <table>
        <thead>
          <tr>
            <th>Plan</th>
            <th>Rate Limit</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Free</td>
            <td>100 requests/minute</td>
          </tr>
          <tr>
            <td>Pro</td>
            <td>1,000 requests/minute</td>
          </tr>
          <tr>
            <td>Enterprise</td>
            <td>Custom</td>
          </tr>
        </tbody>
      </table>
      <p>Rate limit headers are included in all responses:</p>
      <CodeBlock
        language="bash"
        code={`X-RateLimit-Limit: 1000
X-RateLimit-Remaining: 999
X-RateLimit-Reset: 1705313400`}
      />
    </article>
  );
}
